import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { Sentry } from "@/lib/sentry";
import { log } from "@/lib/log";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up listener FIRST to avoid race conditions
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        Sentry.setUser({ id: session.user.id, email: session.user.email });
      } else {
        Sentry.setUser(null);
      }

      if (event === "SIGNED_OUT") {
        // Defensive: clear any stale state
        setSession(null);
        setUser(null);
        Sentry.setUser(null);
      }
    });

    // THEN check for existing session, defensively
    (async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          log.warn("auth.getSession error", { message: error.message, code: (error as { code?: string }).code });
          if (
            error.message?.toLowerCase().includes("refresh token") ||
            (error as { code?: string }).code === "refresh_token_not_found"
          ) {
            await supabase.auth.signOut({ scope: "local" });
          }
          setSession(null);
          setUser(null);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
          if (session?.user) {
            Sentry.setUser({ id: session.user.id, email: session.user.email });
          }
        }
      } catch (err) {
        log.warn("auth.session_restore_failed", { error: err instanceof Error ? err.message : String(err) });
        try {
          await supabase.auth.signOut({ scope: "local" });
        } catch {
          // ignore
        }
        setSession(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      void supabase.functions.invoke("log-auth-event", { body: { event: "signed_out" } });
    } catch {
      // ignore
    }
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
